import { SearchInput } from "../../components/SearchInput";
import { Toast } from "../../utils/Toast";
import { t } from "../../utils/I18n";
import { AosInitializer } from "./AosInitializer";

/**
 * Insight/Portfolio 공개 목록의 GET 전환을 전체 새로고침 없이 처리한다.
 * 서버 HTML을 정본으로 유지하고 동일 data-public-list root의 내부만 교체한다.
 */
export class PublicListController {
    private static initialized = false;
    private static instances: PublicListController[] = [];
    private loading = false;

    private constructor(private readonly root: HTMLElement) {
        this.root.addEventListener("submit", (event) => this.onSubmit(event));
        this.root.addEventListener("click", (event) => this.onClick(event));
    }

    static init(): void {
        if (PublicListController.initialized) return;

        document.querySelectorAll<HTMLElement>("[data-public-list]").forEach((root) => {
            PublicListController.instances.push(new PublicListController(root));
        });

        window.addEventListener("popstate", () => {
            PublicListController.instances.forEach((controller) => {
                void controller.load(window.location.href, false, false);
            });
        });
        PublicListController.initialized = true;
    }

    private onSubmit(event: SubmitEvent): void {
        const form = event.target instanceof HTMLFormElement
            ? event.target.closest<HTMLFormElement>("[data-public-list-form]")
            : null;
        if (!form || !this.root.contains(form)) return;

        event.preventDefault();
        void this.load(this.urlFromForm(form), true, true);
    }

    private onClick(event: MouseEvent): void {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const clear = target.closest<HTMLElement>("[data-search-clear]");
        if (clear) {
            const form = clear.closest<HTMLFormElement>("[data-public-list-form]");
            if (form) {
                event.preventDefault();
                void this.load(this.urlFromForm(form), true, true, true);
            }
            return;
        }

        const link = target.closest<HTMLAnchorElement>(
            "[data-public-list-link], .no-pagination a[data-page]",
        );
        if (!link || !this.root.contains(link)) return;
        if (link.hasAttribute("disabled") || link.getAttribute("aria-disabled") === "true") {
            event.preventDefault();
            return;
        }

        event.preventDefault();
        void this.load(link.href, true, true);
    }

    private urlFromForm(form: HTMLFormElement): string {
        const url = new URL(form.action || window.location.href, window.location.href);
        url.search = "";
        const values = new FormData(form);
        values.forEach((value, key) => {
            if (typeof value === "string" && value !== "") {
                url.searchParams.append(key, value);
            }
        });
        url.searchParams.delete("page");
        return url.toString();
    }

    private async load(
        href: string,
        pushHistory: boolean,
        scrollToList: boolean,
        focusSearch = false,
    ): Promise<void> {
        if (this.loading) return;

        this.loading = true;
        this.root.classList.add("is-loading");
        this.root.setAttribute("aria-busy", "true");
        const loader = Toast.loading(t("list.loading"), `public-list-${this.root.dataset.publicList ?? "default"}`);

        try {
            const response = await fetch(href, {
                method: "GET",
                credentials: "same-origin",
                headers: {
                    "X-Requested-With": "fetch",
                    "Accept": "text/html",
                },
            });
            if (!response.ok) {
                throw new Error(`Public list request failed: ${response.status}`);
            }

            const html = await response.text();
            const nextDocument = new DOMParser().parseFromString(html, "text/html");
            const key = this.root.dataset.publicList ?? "";
            const nextRoot = nextDocument.querySelector<HTMLElement>(`[data-public-list="${CSS.escape(key)}"]`);
            if (!nextRoot) {
                throw new Error(`Public list root not found: ${key}`);
            }

            this.root.innerHTML = nextRoot.innerHTML;
            if (pushHistory) {
                window.history.pushState({ publicList: key }, "", href);
            }

            SearchInput.initAll(this.root);
            window.dispatchEvent(new CustomEvent("template:components-init"));
            AosInitializer.refresh();

            if (scrollToList) {
                const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
                this.root.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
            }
            if (focusSearch) {
                requestAnimationFrame(() => {
                    this.root.querySelector<HTMLInputElement>("[data-search-input-field]")?.focus();
                });
            }
        } catch (error) {
            console.error("[PublicListController]", error);
            Toast.error(t("list.load_error"));
        } finally {
            loader.dismiss();
            this.root.classList.remove("is-loading");
            this.root.removeAttribute("aria-busy");
            this.loading = false;
        }
    }
}
